import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useState } from "react";
import { fmtMoney } from "../../../utils/NumberTool";
import { InstallmentApplication } from "./InstallmentApplication";

const GREEN = "#00C853";
const DARK = "#0D1420";

interface PaymentDialogProps {
  open: boolean;
  onClose: () => void;
  installment: InstallmentApplication;
  isAdmin: boolean;
  totalAmount: number;
  interestAmount: number;
  capitalAmount: number;
  onSubmit: (data: {
    interestAmount: number;
    capitalAmount: number;
    isPaymentInstallment: boolean;
    isRejected: boolean;
    rejectReason?: string;
  }) => Promise<void>;
}

type PaymentMode = "FULL" | "INTEREST_ONLY" | "CAPITAL_ONLY";

export function PaymentDialog({
  open,
  onClose,
  installment,
  isAdmin,
  totalAmount,
  interestAmount,
  capitalAmount,
  onSubmit,
}: PaymentDialogProps) {
  const [paymentMode, setPaymentMode] = useState<PaymentMode>("FULL");
  const [customCapital, setCustomCapital] = useState(capitalAmount);
  const [customInterest, setCustomInterest] = useState(interestAmount);
  const [isRejected, setIsRejected] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleModeChange = (mode: PaymentMode) => {
    setPaymentMode(mode);
    setIsRejected(false);
    if (mode === "FULL") {
      setCustomCapital(capitalAmount);
      setCustomInterest(interestAmount);
    } else if (mode === "INTEREST_ONLY") {
      setCustomCapital(0);
      setCustomInterest(interestAmount);
    } else if (mode === "CAPITAL_ONLY") {
      setCustomCapital(capitalAmount);
      setCustomInterest(0);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({
        interestAmount: isRejected ? 0 : customInterest,
        capitalAmount: isRejected ? 0 : customCapital,
        isPaymentInstallment: paymentMode === "FULL" || paymentMode === "INTEREST_ONLY",
        isRejected,
        rejectReason: isRejected ? rejectReason : undefined,
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}>
      {/* Header */}
      <Box sx={{ bgcolor: DARK, px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontWeight: 800, fontSize: 18, color: "white" }}>Aprobar pago</Typography>
          <Typography sx={{ fontSize: 12, color: "#8899AA" }}>
            {new Date(installment.createdAt).toLocaleDateString("es-CO", { day: "numeric", month: "long", year: "numeric" })}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "white", bgcolor: "#ffffff18" }}>
          <Close fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2, bgcolor: "#f7f9f7" }}>

        {/* Comprobante */}
        {installment.url && (
          <Box
            component="a" href={installment.url} target="_blank" rel="noopener"
            sx={{ display: "block", borderRadius: 2, overflow: "hidden", border: "1px solid #e0e0e0", textDecoration: "none", mb: 2 }}
          >
            <Box component="img" src={installment.url} alt="Comprobante" sx={{ width: "100%", maxHeight: 220, objectFit: "cover", display: "block" }} />
            <Typography sx={{ fontSize: 11, color: "#8899AA", textAlign: "center", py: 0.5, bgcolor: "white" }}>
              Ver comprobante completo →
            </Typography>
          </Box>
        )}

        {isAdmin && installment.installmentStatus === "PENDING" && !isRejected && (
          <>
            {/* Tipo de pago */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>
              Tipo de pago
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              {[
                { value: "FULL" as PaymentMode, label: "Cuota completa" },
                { value: "INTEREST_ONLY" as PaymentMode, label: "Solo interés" },
                { value: "CAPITAL_ONLY" as PaymentMode, label: "Abono capital" },
              ].map((opt) => (
                <Box
                  key={opt.value}
                  onClick={() => handleModeChange(opt.value)}
                  sx={{ flex: 1, p: 1, borderRadius: 2, textAlign: "center", cursor: "pointer", border: `2px solid ${paymentMode === opt.value ? GREEN : "#e0e0e0"}`, bgcolor: paymentMode === opt.value ? `${GREEN}15` : "white", transition: "all 0.2s" }}
                >
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: paymentMode === opt.value ? GREEN : "#667788" }}>{opt.label}</Typography>
                </Box>
              ))}
            </Stack>

            {/* Desglose */}
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#8899AA", letterSpacing: 1.5, textTransform: "uppercase", mb: 1 }}>
              Desglose
            </Typography>
            <Stack spacing={1.5} sx={{ mb: 2 }}>
              <Box sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#667788", fontWeight: 600 }}>Capital</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: DARK }}>{fmtMoney(customCapital)}</Typography>
              </Box>
              <Box sx={{ bgcolor: "white", borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography sx={{ fontSize: 13, color: "#667788", fontWeight: 600 }}>Interés</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: "#f59e0b" }}>{fmtMoney(customInterest)}</Typography>
              </Box>
              <Box sx={{ bgcolor: `${GREEN}11`, borderRadius: 2, p: 1.5, display: "flex", justifyContent: "space-between", alignItems: "center", border: `1px solid ${GREEN}33` }}>
                <Typography sx={{ fontSize: 13, color: GREEN, fontWeight: 700 }}>Total</Typography>
                <Typography sx={{ fontSize: 16, fontWeight: 800, color: GREEN }}>{fmtMoney(customCapital + customInterest)}</Typography>
              </Box>
            </Stack>

            {/* Campo capital personalizado si es abono */}
            {paymentMode === "CAPITAL_ONLY" && (
              <TextField
                fullWidth label="Monto del abono"
                type="number"
                value={customCapital}
                onChange={(e) => setCustomCapital(Number(e.target.value))}
                sx={{ mb: 2, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
            )}
          </>
        )}

        {/* Razón de rechazo */}
        {isRejected && (
          <TextField
            fullWidth label="Motivo del rechazo"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            sx={{ mt: 1, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
          />
        )}

        {/* Info si ya está aprobado/rechazado */}
        {installment.installmentStatus !== "PENDING" && (
          <Box sx={{ bgcolor: "white", borderRadius: 2, p: 2 }}>
            <Stack spacing={1}>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 13, color: "#667788" }}>Capital</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: DARK }}>{fmtMoney(installment.capitalAmount)}</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 13, color: "#667788" }}>Interés</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#f59e0b" }}>{fmtMoney(installment.interestAmount)}</Typography>
              </Box>
              {installment.installmentStatus === "REJECTED" && installment.rejectReason && (
                <Box sx={{ bgcolor: "#ef444411", borderRadius: 2, p: 1.5, mt: 1 }}>
                  <Typography sx={{ fontSize: 12, color: "#ef4444", fontWeight: 600 }}>Razón de rechazo: {installment.rejectReason}</Typography>
                </Box>
              )}
            </Stack>
          </Box>
        )}
      </DialogContent>

      {/* Botones */}
      {isAdmin && installment.installmentStatus === "PENDING" && (
        <Box sx={{ px: 2.5, py: 2, display: "flex", gap: 1.5, bgcolor: "white", borderTop: "1px solid #f0f0f0" }}>
          <Button
            fullWidth onClick={() => { setIsRejected(true); }}
            sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #ef444433", color: "#ef4444" }}
          >
            Rechazar
          </Button>
          {isRejected ? (
            <Button
              fullWidth variant="contained" onClick={handleSubmit}
              disabled={loading || !rejectReason}
              sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: "#ef4444", boxShadow: "none", "&:hover": { bgcolor: "#dc2626" } }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Confirmar rechazo"}
            </Button>
          ) : (
            <Button
              fullWidth variant="contained" onClick={handleSubmit}
              disabled={loading}
              sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 700, bgcolor: GREEN, boxShadow: "none", "&:hover": { bgcolor: "#00b048" } }}
            >
              {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "✓ Aprobar pago"}
            </Button>
          )}
        </Box>
      )}

      {(!isAdmin || installment.installmentStatus !== "PENDING") && (
        <Box sx={{ px: 2.5, py: 2, bgcolor: "white", borderTop: "1px solid #f0f0f0" }}>
          <Button fullWidth onClick={onClose} sx={{ borderRadius: "50px", textTransform: "none", fontWeight: 600, border: "1px solid #e0e0e0", color: "#667788" }}>
            Cerrar
          </Button>
        </Box>
      )}
    </Dialog>
  );
}
